import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiHeart, FiCalendar, FiEdit, FiEye, FiExternalLink, FiClock, FiTarget, FiCode } from 'react-icons/fi';
import { format } from 'date-fns';

const ProblemCard = ({ problem, onToggleFavorite, onScheduleRevision }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getDifficultyConfig = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return {
          color: 'bg-gradient-to-r from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30',
          text: 'text-green-700 dark:text-green-300',
          border: 'border-green-200 dark:border-green-700',
          icon: '🟢'
        };
      case 'Medium':
        return {
          color: 'bg-gradient-to-r from-yellow-100 to-orange-200 dark:from-yellow-900/30 dark:to-orange-800/30',
          text: 'text-orange-700 dark:text-orange-300',
          border: 'border-orange-200 dark:border-orange-700',
          icon: '🟡'
        };
      case 'Hard':
        return {
          color: 'bg-gradient-to-r from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30',
          text: 'text-red-700 dark:text-red-300',
          border: 'border-red-200 dark:border-red-700',
          icon: '🔴'
        };
      default:
        return {
          color: 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700',
          text: 'text-gray-700 dark:text-gray-300',
          border: 'border-gray-200 dark:border-gray-600',
          icon: '⚪'
        };
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Solved':
        return {
          color: 'bg-gradient-to-r from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30',
          text: 'text-primary-700 dark:text-primary-300',
          border: 'border-primary-200 dark:border-primary-700',
          icon: '✅'
        };
      case 'In Progress':
        return {
          color: 'bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30',
          text: 'text-blue-700 dark:text-blue-300',
          border: 'border-blue-200 dark:border-blue-700',
          icon: '🔄'
        };
      case 'For Review':
        return {
          color: 'bg-gradient-to-r from-purple-100 to-purple-200 dark:from-purple-900/30 dark:to-purple-800/30',
          text: 'text-purple-700 dark:text-purple-300',
          border: 'border-purple-200 dark:border-purple-700',
          icon: '👀'
        };
      default:
        return {
          color: 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700',
          text: 'text-gray-700 dark:text-gray-300',
          border: 'border-gray-200 dark:border-gray-600',
          icon: '📝'
        };
    }
  };

  const difficultyConfig = getDifficultyConfig(problem.platformDifficulty);
  const statusConfig = getStatusConfig(problem.status);

  const getPlatformIcon = (platform) => {
    const icons = {
      'LeetCode': '🟠',
      'HackerRank': '🟢',
      'CodeForces': '🔵',
      'GeeksforGeeks': '🟡',
      'InterviewBit': '🟣',
      'AtCoder': '🟤',
      'TopCoder': '🔴',
      'SPOJ': '⚫',
    };
    return icons[platform] || '💻';
  };

  return (
    <div 
      className="group relative bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-purple-50/50 dark:from-primary-900/10 dark:via-transparent dark:to-purple-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* Favorite Status Indicator */}
      {problem.isFavorite && (
        <div className="absolute top-4 left-4 w-3 h-3 bg-gradient-to-r from-red-500 to-pink-500 rounded-full animate-pulse shadow-lg" />
      )}

      <div className="relative p-6 space-y-4">
        {/* Header with badges and favorite */}
        <div className="flex items-start justify-between">
          <div className="flex flex-wrap gap-2">
            <div className={`inline-flex items-center px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all duration-200 ${difficultyConfig.color} ${difficultyConfig.text} ${difficultyConfig.border}`}>
              <span className="mr-1.5">{difficultyConfig.icon}</span>
              {problem.platformDifficulty}
            </div>
            <div className={`inline-flex items-center px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all duration-200 ${statusConfig.color} ${statusConfig.text} ${statusConfig.border}`}>
              <span className="mr-1.5">{statusConfig.icon}</span>
              {problem.status}
            </div>
          </div>
          
          <button
            onClick={() => onToggleFavorite(problem._id)}
            className={`p-2 rounded-xl transition-all duration-200 transform hover:scale-110 ${
              problem.isFavorite
                ? 'text-red-500 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
                : 'text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
            }`}
          >
            <FiHeart className={`w-5 h-5 ${problem.isFavorite ? 'fill-current' : ''}`} />
          </button>
        </div>

        {/* Problem Title */}
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2 group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors duration-200">
            {problem.problemName}
          </h3>
          
          {problem.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 leading-relaxed">
              {problem.description}
            </p>
          )}
        </div>

        {/* Problem Details */}
        <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-1.5">
            <span className="text-base">{getPlatformIcon(problem.platform)}</span>
            <span className="font-medium">{problem.platform}</span>
          </div>
          
          <div className="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
          
          <div className="flex items-center space-x-1.5">
            <FiTarget className="w-4 h-4" />
            <span>{problem.mainCategory}</span>
          </div>
          
          <div className="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
          
          <div className="flex items-center space-x-1.5">
            <FiClock className="w-4 h-4" />
            <span>{problem.timeTaken}m</span>
          </div>
        </div>

        {/* Problem Pattern */}
        {problem.problemPattern && (
          <div>
            <div className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-100 to-indigo-200 dark:from-indigo-900/30 dark:to-indigo-800/30 text-indigo-700 dark:text-indigo-300 text-xs font-semibold rounded-xl border border-indigo-200 dark:border-indigo-700">
              <FiCode className="w-3 h-3 mr-1.5" />
              {problem.problemPattern}
            </div>
          </div>
        )}

        {/* Topic Tags */}
        {problem.topicTags && problem.topicTags.length > 0 && (
          <div>
            <div className="flex flex-wrap gap-1.5">
              {problem.topicTags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-lg border border-blue-200/50 dark:border-blue-700/50 transition-colors duration-200 hover:from-blue-100 hover:to-cyan-100"
                >
                  #{tag}
                </span>
              ))}
              {problem.topicTags.length > 3 && (
                <span className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 text-gray-600 dark:text-gray-400 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600">
                  +{problem.topicTags.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Footer with date and actions */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
          <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
            {format(new Date(problem.solveDate), 'MMM dd, yyyy')}
          </div>
          
          <div className="flex items-center space-x-1">
            <Link
              to={`/problems/${problem._id}/edit`}
              className="p-2 rounded-lg text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-200 transform hover:scale-110"
              title="Edit Problem"
            >
              <FiEdit className="w-4 h-4" />
            </Link>
            
            <button
              onClick={() => window.open(problem.problemLink, '_blank')}
              className="p-2 rounded-lg text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-200 transform hover:scale-110"
              title="View Problem"
            >
              <FiExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Revision Schedule */}
        {problem.revisionSchedule?.nextRevisionDate && (
          <div className="pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-200/50 dark:border-amber-700/50">
              <div className="flex items-center text-sm text-amber-700 dark:text-amber-300">
                <FiCalendar className="w-4 h-4 mr-2" />
                <span className="font-medium">Next revision:</span>
                <span className="ml-1">
                  {format(new Date(problem.revisionSchedule.nextRevisionDate), 'MMM dd, yyyy')}
                </span>
              </div>
              
              <button
                onClick={() => onScheduleRevision(problem._id)}
                className="text-xs font-semibold text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 px-2 py-1 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-all duration-200"
              >
                Reschedule
              </button>
            </div>
          </div>
        )}

        {/* Hover Effect Progress Bar */}
        <div className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-300 ${
          isHovered ? 'w-full' : 'w-0'
        }`} />
      </div>

      {/* Floating Action Indicator */}
      {isHovered && (
        <div className="absolute top-4 right-16 animate-in fade-in slide-in-from-right-2 duration-200">
          <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse shadow-lg" />
        </div>
      )}
    </div>
  );
};

export default ProblemCard;